<template>
  <InertiaHead>
    <title>Login</title>
    <meta
      name="description"
      content="Log in to VibeTeams to access your AI-powered teams, manage agents, and streamline workflows for smarter, faster business execution."
    />
    <link rel="canonical" href="https://vibeteams.ai/login" />
  </InertiaHead>

  <AuthBase>
    <div v-if="status" class="mb-4 text-center text-sm font-medium text-green-600">
      {{ status }}
    </div>

    <form
      @submit.prevent="submit"
      class="mx-auto flex w-full max-w-[449px] flex-col gap-4 space-y-5 rounded-2xl border-1 border-eerie-black-400 bg-transparent p-8 sm:gap-5"
    >
      <div class="space-y-2 py-2 sm:py-4">
        <h1 class="text-center font-exo-bold text-xl sm:text-2xl">Welcome Back to the Vibe</h1>
        <p class="px-2 text-center font-segoe-regular text-xs">Jump back in and sync up with your AI crew.</p>
      </div>
      <div class="grid gap-5">
        <div class="grid gap-2">
          <UiLabel for="email">Email address</UiLabel>
          <UiInput
            id="email"
            class="h-[42px] w-full rounded-lg border-0 bg-gray-700 font-segoe-regular text-sm"
            type="email"
            required
            autofocus
            :tabindex="1"
            autocomplete="email"
            v-model="form.email"
            placeholder="email@example.com"
          />
          <InputError :message="form.errors.email" />
        </div>

        <div class="grid gap-2">
          <UiLabel for="password">Password</UiLabel>
          <UiInput
            id="password"
            class="h-[42px] w-full rounded-lg border-0 bg-gray-700 font-segoe-regular text-sm"
            type="password"
            required
            :tabindex="2"
            autocomplete="current-password"
            v-model="form.password"
            placeholder="Password"
          />
          <InputError :message="form.errors.password" />
        </div>

        <div class="flex items-center justify-between">
          <UiLabel for="remember" class="flex items-center font-segoe-regular">
            <Checkbox class="border-0 bg-gray-700" id="remember" v-model="form.remember" :tabindex="3" />
            <span class="ml-1 text-gray-300">Remember me</span>
          </UiLabel>
          <TextLink
            v-if="canResetPassword"
            :href="route('password.request')"
            class="font-segoe-regular text-sm text-navy-blue-500 !no-underline"
            :tabindex="4"
          >
            Forgot password?
          </TextLink>
        </div>

        <div class="space-y-4">
          <UiButton variant="gradient" type="submit" class="h-[43px] w-full text-sm" :tabindex="5" :disabled="form.processing">
            <LoaderCircle v-if="form.processing" class="h-4 w-4 animate-spin" />
            Sign in
          </UiButton>
        </div>
        <div class="grid grid-cols-3 items-center justify-center">
          <hr class="text-[#353535]" />
          <div class="flex justify-center">
            <span class="mx-4 text-center font-segoe-regular text-xs text-[#909090]">or sign in with</span>
          </div>
          <hr class="text-[#353535]" />
        </div>
        <a
          :href="route('auth.redirect', 'google')"
          :tabindex="6"
          class="flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-[#353535] bg-[#1C1C1C] px-5 py-2"
        >
          <img :src="Google" alt="Google Logo" class="h-6 w-6" />
          <span class="text-center font-segoe-bold text-sm text-white">Google</span>
        </a>

        <div class="text-left font-segoe-regular text-sm">
          New to the vibe?
          <a :href="route('register')" :tabindex="7" class="font-segoe-regular text-navy-blue-500 !no-underline">Join here</a>
        </div>
      </div>
    </form>
  </AuthBase>
</template>

<script lang="ts">
import Google from '@/../../resources/images/login-register/google.svg';
import InputError from '@/components/common/InputError.vue';
import TextLink from '@/components/common/TextLink.vue';
import { Button as UiButton } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input as UiInput } from '@/components/ui/input';
import { Label as UiLabel } from '@/components/ui/label';
import AuthBase from '@/layouts/AuthLayout.vue';
import { Head as InertiaHead, useForm } from '@inertiajs/vue3';
import { LoaderCircle } from 'lucide-vue-next';

export default {
  components: {
    InputError,
    TextLink,
    UiButton,
    Checkbox,
    UiInput,
    UiLabel,
    AuthBase,
    InertiaHead,
    LoaderCircle,
  },
  props: {
    status: {
      type: String,
      required: false,
    },
    canResetPassword: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      form: useForm({
        email: '',
        password: '',
        remember: false,
      }),
      Google,
    };
  },
  methods: {
    submit() {
      this.form.post(route('login.store'), {
        onFinish: () => this.form.reset('password'),
      });
    },
  },
};
</script>
